import { merge } from "lodash";
import { v4 } from "uuid";

export type WordProcessorFeatureOptions = {
  isHostedTinyMCEVersionEnabled?: boolean;
  readOnly?: boolean;
  allowUndoRedo?: boolean;
  allowHtml?: boolean;
  allowLists?: boolean;
  allowAlignments?: boolean;
  allowTables?: boolean;
  allowImages?: boolean;
  allowTemplates?: boolean;
  allowSignDocument?: boolean;
  allowMergeTags?: boolean;
  allowInsertMergeTagsValues?: boolean;
  allowPasteAsText?: boolean;
  allowCode?: boolean;
  allowRemoveFormat?: boolean;
  allowFullscreen?: boolean;
  allowPreview?: boolean;
  allowSpeechToText?: boolean;
  allowToolbarAutohide?: boolean;
  allowShowLength?: boolean;
  allowSelectInvoice?: boolean;
  allowInvoiceMergeTags?: boolean;
  allowAIPromptInterface?: boolean;
  allowImageAutoUpload?: boolean;
};

export type WordProcessorFeaturesGroup =
  | "readOnly"
  | "textOnly"
  | "light"
  | "default"
  | "default+sign"
  | "template";

export const getFeatureOptions = (
  features: WordProcessorFeaturesGroup,
  options?: WordProcessorFeatureOptions
) => {
  const result: WordProcessorFeatureOptions & { optionsUpdated: number } =
    merge(
      {
        allowUndoRedo: true,
        allowHtml: true,
        allowLists: true,
        allowAlignments: true,
        allowTables: true,
        allowImages: true,
        allowCode: true,
        allowRemoveFormat: true,
        allowFullscreen: true,
        allowPreview: false,
        allowSpeechToText: true,
        allowPasteAsText: true,
        readOnly: false,
        allowTemplates: false,
        allowSignDocument: false,
        allowMergeTags: false,
        allowInsertMergeTagsValues: false,
        allowToolbarAutohide: true,
        allowShowLength: false,
        allowSelectInvoice: false,
        allowInvoiceMergeTags: false,
        allowAIPromptInterface: false,
        allowImageAutoUpload: false
      },
      features === "readOnly"
        ? {
            allowLists: false,
            allowAlignments: false,
            allowTables: false,
            allowImages: false,
            allowCode: true,
            allowRemoveFormat: false,
            allowSpeechToText: false,
            allowFullscreen: true,
            allowPreview: false,
            allowPasteAsText: false,
            readOnly: true,
            allowSelectInvoice: false
          }
        : {},
      features === "textOnly"
        ? {
            allowHtml: false,
            allowLists: false,
            allowAlignments: false,
            allowTables: false,
            allowImages: false,
            allowRemoveFormat: false,
            allowFullscreen: false,
            allowPreview: false,
            allowTemplates: true
          }
        : {},
      features === "light"
        ? {
            allowLists: false,
            allowAlignments: false,
            allowImages: false,
            allowRemoveFormat: false,
            allowCode: false,
            allowFullscreen: false,
            allowPreview: false,
            allowTemplates: true,
            allowToolbarAutohide: false
          }
        : {},
      features === "default"
        ? {
            allowTemplates: true,
            allowMergeTags: true,
            allowInsertMergeTagsValues: true
          }
        : {},
      features === "default+sign"
        ? {
            allowTemplates: true,
            allowMergeTags: true,
            allowInsertMergeTagsValues: true,
            allowSignDocument: true
          }
        : {},
      features === "template"
        ? {
            allowTemplates: true,
            allowMergeTags: true
          }
        : {},
      options,
      {
        optionsUpdated: new Date().getTime()
      }
    );

  return result;
};

export const getToolbarFeatures = ({
  readOnly,
  allowUndoRedo,
  allowHtml,
  allowImages,
  allowTables,
  allowTemplates,
  allowSignDocument,
  allowLists,
  allowAlignments,
  allowCode,
  allowMergeTags,
  allowRemoveFormat,
  allowPasteAsText,
  allowPreview,
  allowFullscreen,
  allowSpeechToText
}: WordProcessorFeatureOptions) => {
  if (readOnly) return false;

  return [
    ["undo redo |", allowUndoRedo],
    ["speech_to_text_button |", allowSpeechToText],
    ["mergetags |", allowMergeTags],
    ["suno_templates_button |", allowTemplates],
    ["suno_sign_document_button |", allowSignDocument && allowHtml],
    ["blocks fontsizeinput |", allowHtml],
    ["bold italic underline forecolor backcolor | pagebreak hr |", allowHtml],
    ["align", allowAlignments && allowHtml],
    ["bullist checklist numlist outdent indent |", allowLists && allowHtml],
    ["link", allowHtml],
    ["image", allowImages && allowHtml],
    ["table tablecellbackgroundcolor |", allowTables && allowHtml],
    ["removeformat", allowRemoveFormat && allowHtml],
    ["pastetext", allowPasteAsText],
    ["|", true],
    ["code |", allowCode && allowHtml],
    ["suno_fullscreen_button |", allowFullscreen],
    ["suno_preview_button", allowPreview]
  ]
    .filter(([key, value]) => Boolean(value))
    .map(([key, value]) => key)
    .join(" ");
};

export const getPlugins = ({
  readOnly,
  allowHtml,
  allowImages,
  allowTables,
  allowLists,
  allowCode,
  allowMergeTags,
  allowPreview,
  allowFullscreen
}: WordProcessorFeatureOptions) => {
  return [
    ["inlinecss", true],
    ["pagebreak", true],
    ["quickbars", !readOnly && allowHtml],
    ["table", !readOnly && allowTables && allowHtml],
    ["advtable", !readOnly && allowTables && allowHtml],
    ["lists", !readOnly && allowLists && allowHtml],
    ["advlist", !readOnly && allowLists && allowHtml],
    ["checklist", !readOnly && allowLists && allowHtml],
    ["link", !readOnly && allowHtml],
    ["image", !readOnly && allowImages && allowHtml],
    ["editimage", !readOnly && allowImages && allowHtml],
    ["code", !readOnly && allowCode && allowHtml],
    ["mergetags", !readOnly && allowMergeTags],
    ["fullscreen", allowFullscreen],
    ["preview", allowPreview],
    ["searchreplace", !readOnly],
    ["autolink", !readOnly]
  ]
    .filter(([key, value]) => Boolean(value))
    .map(([key, value]) => key) as string[];

  // "charmap",
  // "insertdatetime",
  // "media",
  // "visualblocks",
  // "wordcount",
  // "nonbreaking",
  // "pagebreak",
};

export const showToolbar = (id: string) => {
  const headerElement = document.querySelector(
    `#${id} + div .tox-editor-header`
  ) as HTMLElement;
  if (headerElement) {
    headerElement.style.display = "unset";
    headerElement.style.visibility = "visible";
  }
};

export const hideToolbar = (id: string) => {
  const headerElement = document.querySelector(
    `#${id} + div .tox-editor-header`
  ) as HTMLElement;
  if (headerElement) {
    headerElement.style.display = "none";
  }
};

export const getUniqueId = () => `wp-${v4()}`;
